"use client";

import type React from "react";
import { useState } from "react";
import {
  FormControl,
  FormLabel,
  Text,
  Switch,
  HStack,
  Box,
  IconButton,
  Tooltip,
} from "@chakra-ui/react";
import { InfoIcon } from "@chakra-ui/icons";
import { useAppTheme } from "../../../ThemeContext.tsx";

interface TranscriptFormattingToggleProps {
  value: boolean | undefined;
  onChange: (value: boolean) => Promise<boolean> | void;
}

export const TranscriptFormattingToggle: React.FC<
  TranscriptFormattingToggleProps
> = ({ value = true, onChange }) => {
  const [isUpdating, setIsUpdating] = useState(false);
  const { resolvedTheme } = useAppTheme();
  const isDark = resolvedTheme === "dark";

  const handleChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    setIsUpdating(true);
    try {
      await onChange(!e.target.checked); // Note: We invert the value because the UI shows "Disable" but the API expects "enable"
    } finally {
      setIsUpdating(false);
    }
  };

  return (
    <FormControl>
      <HStack justify="space-between" align="flex-start">
        <Box>
          <HStack align="center" mb={1}>
            <FormLabel
              fontSize="sm"
              fontWeight="medium"
              color={isDark ? "gray.300" : "gray.700"}
              mb={0}
            >
              Disable Transcript Formatting
            </FormLabel>
            <Tooltip
              label="Prevents automatic formatting of numbers, dates, etc. in transcripts"
              placement="top"
            >
              <IconButton
                aria-label="Info"
                icon={<InfoIcon />}
                size="xs"
                variant="ghost"
                color={isDark ? "gray.400" : "gray.600"}
                marginLeft={-5}
              />
            </Tooltip>
          </HStack>
          <Text fontSize="xs" color={isDark ? "gray.400" : "gray.600"}>
            Prevent agent errors like phone numbers being formatted as
            timestamps.
          </Text>
        </Box>
        <Switch
          isChecked={!value} // Note: We invert the value because the UI shows "Disable" but the API expects "enable"
          onChange={handleChange}
          isDisabled={isUpdating}
          colorScheme={isDark ? "whiteAlpha" : "blackAlpha"}
          borderColor={isDark ? "gray.500" : "gray.400"}
          sx={{
            ".chakra-switch__track": {
              backgroundColor: !value
                ? isDark
                  ? "gray.600"
                  : "gray.400" // Darker color when turned on
                : isDark
                ? "gray.700"
                : "gray.300", // Color when turned off
            },
          }}
        />
      </HStack>
    </FormControl>
  );
};
